import { Injectable, WritableSignal, Signal, computed, signal, inject } from '@angular/core';
import { ApiService } from '../services/api-services/api.service';
import { Place, FirstAider, FilterCriteria } from '../interfaces/interfaces';
import { catchError, finalize, of, tap, firstValueFrom } from 'rxjs';
import { PlacesStore } from './places.store';

@Injectable({
  providedIn: 'root'
})
export class FirstAiderStore {
  // Signals
  private _allFirstAidersSignal: WritableSignal<FirstAider[]> = signal<FirstAider[]>([]);
  private _loadingSignal: WritableSignal<boolean> = signal<boolean>(true);
  private _errorSignal: WritableSignal<string | null> = signal<string | null>(null);
  private _isFeatureDisabledOrSelectedGroupIsEmptySignal: WritableSignal<boolean> = signal<boolean>(false);
  // Computed signals
  public firstAiders: Signal<FirstAider[]> = this._allFirstAidersSignal.asReadonly();

  public loading: Signal<boolean> = this._loadingSignal.asReadonly(); 
  public error: Signal<string | null> = this._errorSignal.asReadonly();
  public isFeatureDisabledOrSelectedGroupIsEmpty: Signal<boolean> = this._isFeatureDisabledOrSelectedGroupIsEmptySignal.asReadonly();


  private placesStore: PlacesStore = inject(PlacesStore);
  private apiService: ApiService = inject(ApiService);
  constructor(
  ) {}

  // Actions

  public resetStoreData() {
    this._allFirstAidersSignal.set([]);
    this._errorSignal.set(null);
  }

  // API calls
  public fetchFirstAiders(): Promise<FirstAider[]> {
    this._loadingSignal.set(true);
    this._errorSignal.set(null);
    this._allFirstAidersSignal.set([]);
    return firstValueFrom(this.apiService.getActionGroupMembers().pipe(
      tap(async (firstAiders: FirstAider[]) => {
        if(firstAiders.length > 0) {
        // if(this.placesStore.places().length == 0) {
        //   await this.placesStore.fetchAvailablePlaces();
        // }
        this._allFirstAidersSignal.set(this.mapPlacesToMembersLocation(firstAiders));
        this._isFeatureDisabledOrSelectedGroupIsEmptySignal.set(false);
        } else {
          this._isFeatureDisabledOrSelectedGroupIsEmptySignal.set(true);
        }
        this._loadingSignal.set(false);
      }),
      catchError((error) => {
        this._errorSignal.set(error.message || 'Failed to fetch first aiders');
        this._loadingSignal.set(false);
        return of([]);
      })
    ));
  }

  // Helpers
  public findFirstAider(officer: FirstAider, query: string, filterCriteria: FilterCriteria = null): boolean {
    let isMatchingQuery = true;
    if(query != '') {
    const groupMemberValues = Object.values(officer.groupMember).map(
        (value) => value?.toString().toLowerCase()
      );
      
      const isMatchingGroupMember = groupMemberValues.some(
        (value) => value?.includes(query.toLowerCase())
      );
    
      const formattedAddress = officer.locationToDisplay?.replace(/,/g, '').toLowerCase() || '';
      isMatchingQuery = isMatchingGroupMember || formattedAddress.includes(query.toLowerCase()) 
    }
      if (filterCriteria != null && isMatchingQuery) {
        isMatchingQuery = Object.keys(filterCriteria).every(key => {
          const criteriaValue = filterCriteria[key];
          // If no filter value is set, allow it
          if (!criteriaValue || criteriaValue === '') {
            return true;
          }
          
          if(key == 'tag') {
            return officer.locationAsPlace?.tag.toLowerCase() === criteriaValue.toLowerCase();
          }
          return officer.locationAsPlace?.address[key] === criteriaValue;
        });
      }    
    return isMatchingQuery;
  }

  private mapPlacesToMembersLocation(groupMembers: FirstAider[]): FirstAider[] {
    const mappedMembers: FirstAider[] = [];

    for (const officer of groupMembers) {
      officer.groupMember = officer.groupMember;
      officer.locationAsPlace = this.placesStore.getPlaceByEmailAddress(officer.location);
      officer.locationToDisplay = this.generateOfficerLocation(officer.locationAsPlace);
      officer.canCall = this.canCallOfficer(officer);
      mappedMembers.push(officer);
    }
    return mappedMembers;
  }

  private generateOfficerLocation(place: Place): string {
    if (!place) return 'OfficerFree';
    return `${place.displayName}, ${this.buildAddressString(place)}`;
  }

  private buildAddressString(place: Place): string {
    if (!place) return '';
    const parts = [];
    if (place.address?.street) parts.push(place.address.street);
    if (place.address?.city) parts.push(place.address.city);
    if (place.address?.country) parts.push(place.address.country);
    return parts.join(', ');
  }

  private canCallOfficer(officer: FirstAider): boolean {
    return !!(officer.groupMember.mobilPhone || officer.groupMember.businessPhone);
  }
}
